import {ActionsTransform} from "./ActionsTransform";
import {assistantsTransform} from "../assistants/AssistantsTransform";

import {
  RealTimeBookingListModel,
  RealTimeBookingRecordModel,
  RealTimeBookingTypeModel,
  RealTimeBookingCreateModel,
  RealTimeBookingUpdateModel,
  RealTimeBookingActionConfigModel,
  RealTimeGHLActionModel,
  RealTimeCalComActionModel,
  RealTimeCalComActionTypeModel,
  RealTimeGHLActionTypeModel,
  RealTimeGHLActionStatusModel,
  RealTimeBookingCalComCreateModel,
  RealTimeBookingGHLCreateModel,
} from "@/api/models";
import {
  RealTimeBookingAction,
  PaginatedResponse,
  RealTimeBookingActionConfig,
  RealTimeGHLAction,
  RealTimeCalComAction,
} from "@/api/services";
import {BaseAction} from "@/types/api";

export class ActionBookingTransform extends ActionsTransform<
  RealTimeBookingRecordModel,
  RealTimeBookingCreateModel,
  RealTimeBookingUpdateModel
> {
  public makeGHLConfigModel(dto: RealTimeGHLAction): RealTimeGHLActionModel {
    const model: RealTimeGHLActionModel = {
      type: dto?.type as RealTimeGHLActionTypeModel,
      leadTimezone: dto?.lead_timezone,
      firstAppDate: dto?.first_appt_date,
      days: dto?.days,
      slotsPerDay: dto?.slots_per_day,
      minHoursDiff: dto?.min_hours_diff,
      calendarId: dto?.calendar_id,
      locationId: dto?.location_id,
      contactId: dto?.contact_id,
      title: dto?.title,
      status: dto?.status as RealTimeGHLActionStatusModel,
      assignedUserId: dto?.assigned_user_id,
      address: dto?.address,
      notify: dto?.notify,
    };

    return model;
  }

  public makeCalComConfigModel(
    dto: RealTimeCalComAction,
  ): RealTimeCalComActionModel {
    const model: RealTimeCalComActionModel = {
      type: dto?.type as RealTimeCalComActionTypeModel,
      leadTimezone: dto?.lead_timezone,
      firstAppDate: dto?.first_appt_date,
      days: dto?.days,
      slotsPerDay: dto?.slots_per_day,
      minHoursDiff: dto?.min_hours_diff,
      bookingEmail: dto?.booking_email,
      eventTypeId: dto?.event_type_id,
      sendConfirmationSms: dto?.send_confirmation_sms,
      confirmationSms: dto?.confirmation_sms,
      calendarIntegration: dto?.calendar_integration,
    };

    return model;
  }

  public makeConfigModel(
    dto: RealTimeBookingActionConfig,
  ): RealTimeBookingActionConfigModel {
    const model: RealTimeBookingActionConfigModel =
      dto?.type === RealTimeCalComActionTypeModel.calcom
        ? this.makeCalComConfigModel(dto as RealTimeCalComAction)
        : this.makeGHLConfigModel(dto as RealTimeGHLAction);

    return model;
  }

  public makeRecordModel(
    dto: RealTimeBookingAction & BaseAction,
  ): RealTimeBookingRecordModel {
    const model: RealTimeBookingRecordModel = {
      ...dto,
      type: dto.type as RealTimeBookingTypeModel,
      title: dto.name,
      icon: dto.icon,
      workspaceId: dto.workspace_id,
      assistants: dto.assistants.map(assistant =>
        assistantsTransform.makeEvenCompactModel(assistant),
      ),
      config: this.makeConfigModel(dto.config),
      // nodeData: {
      //   action_id: dto.action_id,
      //   lead_timezone: dto.lead_timezone,
      //   booking_email: dto.booking_email,
      // },
    };

    return model;
  }

  public makeListModel(dto: PaginatedResponse): RealTimeBookingListModel {
    const model: RealTimeBookingListModel = {
      records: dto.items.map(item =>
        this.makeRecordModel(item as RealTimeBookingAction & BaseAction),
      ),
      pagination: this.makePaginationModel(dto),
    };

    return model;
  }

  public makeCalComCreateConfigDto(
    model: RealTimeBookingCalComCreateModel,
  ): RealTimeCalComAction {
    const dto: RealTimeCalComAction = {
      type: RealTimeCalComActionTypeModel.calcom,
      event_type_id: model.eventId,
      lead_timezone: model.timezone ?? "Europe/Berlin",
      first_appt_date: model?.bookingDate?.toISOString(),
      days: model.dayesForOfferingDates,
      slots_per_day: model.slotsPerDay,
      min_hours_diff: model.slotHours,
      booking_email: model.email ?? "test@test.com",
      send_confirmation_sms: model.sendConfirmationSms ?? false,
      confirmation_sms: model?.sendConfirmationSms
        ? model.confirmationMessage
        : "",
      calendar_integration: model.meetingLink,
    };

    return dto;
  }

  public makeGHLCreateConfigDto(
    model: RealTimeBookingGHLCreateModel,
  ): RealTimeGHLAction {
    const dto: RealTimeGHLAction = {
      type: RealTimeGHLActionTypeModel.ghl,
      calendar_id: model.calendarId,
      lead_timezone: model.timezone ?? "Europe/Berlin",
      first_appt_date: model.firstAppDate,
      days: model.days,
      slots_per_day: model.slotsPerDay,
      min_hours_diff: model.minHoursDiff,
      title: model.title,
      status: model.status,
      notify: model.notify,
      location_id: model.locationId,
      contact_id: model.contactId,
      assigned_user_id: model.assignedUserId,
      address: model.address,
    };

    return dto;
  }

  public makeCalComUpdateConfigDto(
    model: RealTimeBookingUpdateModel,
  ): RealTimeCalComAction {
    const dto: RealTimeCalComAction = {
      type: RealTimeCalComActionTypeModel.calcom,
      lead_timezone: model.timezone,
      first_appt_date: model?.bookingDate?.toISOString(),
      days: model.dayesForOfferingDates,
      slots_per_day: model.slotsPerDay,
      min_hours_diff: model.slotHours,
      booking_email: model.email,
      event_type_id: model.id,
      send_confirmation_sms: model.sendConfirmationSms,
      confirmation_sms: model?.sendConfirmationSms
        ? model.confirmationMessage
        : "",
      calendar_integration: model.meetingLink,
    };

    return dto;
  }

  public makeGHLUpdateConfigDto(
    model: RealTimeBookingUpdateModel,
  ): RealTimeGHLAction {
    const dto: RealTimeGHLAction = {
      type: RealTimeGHLActionTypeModel.ghl,
      lead_timezone: model.timezone,
      first_appt_date: model?.bookingDate?.toISOString(),
      days: model.dayesForOfferingDates,
      slots_per_day: model.slotsPerDay,
      min_hours_diff: model.slotHours,
      title: model.title,
      notify: model.notify,
      calendar_id: model.calendarId,
      location_id: model.locationId,
      contact_id: model.contactId,
      assigned_user_id: model.assignedUserId,
      address: model.address,
      status: model.status,
    };

    return dto;
  }

  public makeCreateDto(
    model: RealTimeBookingCreateModel,
  ): RealTimeBookingAction {
    const dto: RealTimeBookingAction = {
      type: "booking",
      name: model.name,
      workspace_id: model.workspaceId,
      config:
        model?.type === RealTimeCalComActionTypeModel.calcom
          ? this.makeCalComCreateConfigDto(
              model.config as RealTimeBookingCalComCreateModel,
            )
          : this.makeGHLCreateConfigDto(
              model.config as RealTimeBookingGHLCreateModel,
            ),
    };

    return dto;
  }

  public makeUpdateDto(
    model: RealTimeBookingUpdateModel,
  ): RealTimeBookingAction {
    const dto: RealTimeBookingAction = {
      type: "booking",
      // name: model.name,
      // icon: model?.icon
      workspace_id: model.workspaceId,
      config:
        model?.type === RealTimeCalComActionTypeModel.calcom
          ? this.makeCalComUpdateConfigDto(model)
          : this.makeGHLUpdateConfigDto(model),
    };

    return dto;
  }
}

export const actionBookingTransform = new ActionBookingTransform();
